import { type ReactElement } from 'react';
import classNames from 'classnames';
import { CheckCircle } from 'components/Icons/CheckCircle';

interface StepperProps {
  /**
   * Total number of steps — required when `steps` is not provided (numbered mode).
   * Ignored when `steps` is provided (derived from steps.length).
   */
  totalSteps?: number;
  /** Current active step (1-based). Steps before this index are "completed". */
  activeStep: number;
  /**
   * Optional step labels. When provided, renders the labeled variant with
   * checkmark icons instead of numbers (e.g. profile progress stepper).
   */
  steps?: string[];
  /** Additional class name(s) applied to the root <ol> for context-specific overrides. */
  className?: string;
}

/**
 * Horizontal stepper with connecting lines.
 *
 * **Numbered mode** (default, used in OnboardingForm):
 * Pass `totalSteps` + `activeStep`. Renders numbered circles — active is outlined,
 * completed is filled, upcoming is faded.
 *
 * **Labeled mode** (used in profile progress):
 * Pass `steps` (string[]) + `activeStep`. Renders each step as a checkmark icon
 * (completed) or empty circle (incomplete) alongside its label.
 */
export const Stepper = ({ totalSteps, activeStep, steps, className }: StepperProps) => {
  const total = steps ? steps.length : (totalSteps ?? 0);
  const isLabeled = Boolean(steps);
  const ariaLabel = isLabeled ? 'Profile completion progress' : 'Onboarding progress';
  const items: ReactElement[] = [];

  for (let step = 1; step <= total; step++) {
    const isCompleted = step < activeStep;
    const isActive = !isLabeled && step === activeStep;

    // Connecting line before every step except the first (numbered mode only)
    if (step > 1 && !isLabeled) {
      items.push(
        <li
          key={`line-${step}`}
          role="presentation"
          aria-hidden="true"
          className={classNames('stepper__line', {
            'stepper__line--completed': step <= activeStep,
          })}
        />
      );
    }

    if (isLabeled) {
      items.push(
        <li
          key={`step-${step}`}
          aria-label={`${steps?.[step - 1]}, ${isCompleted ? 'complete' : 'incomplete'}`}
          aria-current={step === activeStep ? 'step' : undefined}
          className={classNames('stepper__step', {
            'stepper__step--completed': isCompleted,
          })}
        >
          <span className="stepper__icon">
            {isCompleted ? (
              <CheckCircle />
            ) : (
              <span className="stepper__empty-circle" aria-hidden="true" />
            )}
          </span>
          <span className="stepper__label stepper__label--full">{steps?.[step - 1]}</span>
          <span className="stepper__label stepper__label--mobile">
            {step}. {isCompleted ? 'Complete' : 'Incomplete'}
          </span>
        </li>
      );
    } else {
      const stepState = isCompleted ? 'completed' : isActive ? 'current' : 'upcoming';
      items.push(
        <li
          key={`circle-${step}`}
          aria-current={isActive ? 'step' : undefined}
          aria-label={`Step ${step} of ${total}, ${stepState}`}
          className={classNames('stepper__circle', {
            'stepper__circle--active': isActive,
            'stepper__circle--completed': isCompleted,
          })}
        >
          {step}
        </li>
      );
    }
  }

  const rootClassName = classNames('stepper', { 'stepper--labeled': isLabeled }, className);

  if (isLabeled) {
    return (
      <div className={rootClassName} role="list" aria-label={ariaLabel}>
        {items}
      </div>
    );
  }

  return (
    <ol className={rootClassName} aria-label={ariaLabel}>
      {items}
    </ol>
  );
};
